import React, { useState } from 'react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';

import { Button } from '@dashboard/components/ui/button';
import BlockEmailDialog from '@dashboard/components/BlockEmailDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { confirm } from '@dashboard/lib/confirm';
import { useBlockedEmails, useUnblockEmail } from '@dashboard/hooks/useBlockedEmails';

/**
 * Blocking is per address, not per person: the same buyer under a second email
 * is a second block. The wording says so, so a merchant does not read this as
 * disabling the customer record.
 *
 * Customer detail is the only place that offers this. Order detail pages show
 * the blocked badge but deliberately carry no control.
 */
export function useBlockEmailAction(customer) {
  const email = customer?.email;
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const { isBlocked, blockedIdFor } = useBlockedEmails();
  const unblockEmail = useUnblockEmail();
  const blocked = Boolean(email && isBlocked(email));

  async function unblock() {
    const confirmed = await confirm(`Unblock ${email}?`, {
      description: 'This email will be able to complete a checkout on your store again.',
      confirm: 'Unblock',
    });

    if (confirmed) {
      unblockEmail.mutate(blockedIdFor(email));
    }
  }

  const dialog = email ? (
    <BlockEmailDialog
      open={showBlockDialog}
      onClose={() => setShowBlockDialog(false)}
      email={email}
      customerId={customer._id}
    />
  ) : null;

  return {
    email,
    blocked,
    block: () => setShowBlockDialog(true),
    unblock,
    isPending: unblockEmail.isPending,
    dialog,
  };
}

const BlockEmailAction = ({ customer }) => {
  const { email, blocked, block, unblock, isPending, dialog } = useBlockEmailAction(customer);

  if (!email) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="ml-2 flex items-center"
            aria-label={`Actions for ${email}`}
            disabled={isPending}
          >
            <EllipsisVerticalIcon className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {blocked ? (
            <DropdownMenuItem variant="destructive" onSelect={unblock}>
              Unblock email
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={block}>Block email</DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {dialog}
    </>
  );
};

export default BlockEmailAction;
